import './References.css';
import { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCirclePlus } from '@fortawesome/free-solid-svg-icons';
import { useTheme } from '@mui/material/styles';
import { Menu, MenuItem } from '@mui/material';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { useNavigate, useParams } from 'react-router-dom';
import { useCategory } from '../../CategoryContext';
import SortByMenu from '../SortByMenu/SortByMenu';
import WatchGuideLink from '../WatchGuideLink/WatchGuideLink';
import { byString } from '../../utils/sortComparators';
import ReferenceModal from './ReferenceModal';
import ReferenceItem from './ReferenceItem';
import ViewReferenceModal from './ViewReferenceModal';
import CopyReferenceModal from './CopyReferenceModal';
import ViewListModal from './ViewListModal';
import DownloadReferenceList from './DownloadReferenceList';

const logos = [
  { src: 'zotera.svg', alt: 'Zotero', protocol: 'zotero://', website: 'https://www.zotero.org' },
  { src: 'mendeley.svg', alt: 'Mendeley', protocol: 'mendeley://', website: 'https://www.mendeley.com' },
  // EndNote has no well-known URL scheme; the guess falls back to the site.
  { src: 'EndNote.svg', alt: 'EndNote', protocol: 'endnote://', website: 'https://endnote.com' },
];

// Sort options for the reference list (alphabetical on the citation text only).
const REFERENCE_SORT_OPTIONS = [
  { key: 'az', label: 'Alphabetical (A-Z)', comparator: byString((r) => r.reference, 'asc') },
  { key: 'za', label: 'Alphabetical (Z-A)', comparator: byString((r) => r.reference, 'desc') },
];

// Try to launch the desktop app via its URL scheme; if the page doesn't lose
// focus shortly after (i.e. nothing handled the scheme / app not installed),
// open the website in a new tab. Browsers expose no real "is app installed"
// check, so this focus heuristic is the standard approach — behavior varies by
// browser/OS, and the website fallback may be popup-blocked in some browsers.
const openAppOrWebsite = (protocol, website) => {
  const openSite = () => {
    if (website) window.open(website, '_blank', 'noopener,noreferrer');
  };
  if (!protocol) {
    openSite();
    return;
  }

  let appLaunched = false;
  const markLaunched = () => {
    appLaunched = true;
  };
  const onVisibility = () => {
    if (document.hidden) markLaunched();
  };
  window.addEventListener('blur', markLaunched);
  document.addEventListener('visibilitychange', onVisibility);

  setTimeout(() => {
    window.removeEventListener('blur', markLaunched);
    document.removeEventListener('visibilitychange', onVisibility);
    if (!appLaunched) openSite();
  }, 700);

  try {
    window.location.href = protocol;
  } catch {
    openSite();
  }
};

// The open References panel — the right-hand column on the Publications page.
// Controlled by the parent; `onClose` collapses it. References data + mutations
// live in the parent (so the list survives open/close toggles) and are passed in.
const References = ({
  onClose,
  style,
  references = [],
  addReference,
  editReference,
  deleteReference,
  reorderReferences,
}) => {
  const theme = useTheme();
  const { isEditor } = useCategory();
  const navigate = useNavigate();
  const { vertical_id } = useParams();

  const [menuAnchor, setMenuAnchor] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [viewItem, setViewItem] = useState(null);
  const [copyItem, setCopyItem] = useState(null);
  const [viewListOpen, setViewListOpen] = useState(false);

  const closeMenu = () => setMenuAnchor(null);

  const handleAddManually = () => {
    closeMenu();
    setEditItem(null);
    setModalOpen(true);
  };

  const handleAddFromResources = () => {
    closeMenu();
    navigate(`/vertical/${vertical_id}/publications/add-references`);
  };

  const handleEdit = (reference) => {
    setEditItem(reference);
    setModalOpen(true);
  };

  const handleSave = (data) => {
    if (editItem) {
      editReference(editItem._id, data);
    } else {
      addReference(data);
    }
  };

  const handleDragEnd = (result) => {
    if (!result.destination || !isEditor) return;
    const updated = Array.from(references);
    const [moved] = updated.splice(result.source.index, 1);
    updated.splice(result.destination.index, 0, moved);
    reorderReferences(updated);
  };

  // Apply a sort option and persist the new order the same way a drag does.
  const handleSort = (option) => {
    if (!isEditor) return;
    reorderReferences([...references].sort(option.comparator));
  };

  return (
    <div className="references references--open" style={style}>
      <div className="references-watch-guide">
        <WatchGuideLink videoId="using-the-reference-list" label="Watch Guide: Reference List" />
      </div>
      <div className="references-header">
        <div className="references-header-left">
          <h2 className="references-title">References</h2>
          {isEditor && (
            <FontAwesomeIcon
              icon={faCirclePlus}
              color={theme.palette.primary.main}
              className="references-add"
              onClick={(e) => setMenuAnchor(e.currentTarget)}
            />
          )}
          <button type="button" className="references-toggle-link" onClick={onClose}>
            Close
          </button>
          <img
            className="references-toggle-icon"
            src={`${process.env.PUBLIC_URL}/references/Hide Icon.svg`}
            alt="Hide references"
            onClick={onClose}
          />
        </div>

        <div className="references-logos">
          {logos.map((logo) => (
            <img
              key={logo.src}
              src={`${process.env.PUBLIC_URL}/references/${logo.src}`}
              alt={logo.alt}
              title={`Open ${logo.alt}`}
              onClick={() => openAppOrWebsite(logo.protocol, logo.website)}
            />
          ))}
        </div>
      </div>

      {references.length > 0 && (
        <div className="references-view-list">
          <button
            type="button"
            className="references-link-btn"
            onClick={() => setViewListOpen(true)}
          >
            View Entire Reference List ({references.length} Total)
          </button>
        </div>
      )}

      {isEditor && references.length > 1 && (
        <div className="references-sort">
          <SortByMenu sections={[{ options: REFERENCE_SORT_OPTIONS }]} onSelect={handleSort} />
        </div>
      )}

      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={closeMenu}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      >
        <MenuItem onClick={handleAddManually}>Add Manually</MenuItem>
        <MenuItem onClick={handleAddFromResources}>Add from Existing Vertical Resource(s)</MenuItem>
      </Menu>

      <div className="references-body">
        <DragDropContext onDragEnd={handleDragEnd}>
          <Droppable droppableId="references">
            {(provided) => (
              <div {...provided.droppableProps} ref={provided.innerRef}>
                {references.map((reference, index) => (
                  <Draggable
                    key={reference._id}
                    draggableId={reference._id}
                    index={index}
                    isDragDisabled={!isEditor}
                  >
                    {(dragProvided) => (
                      <div
                        ref={dragProvided.innerRef}
                        {...dragProvided.draggableProps}
                        {...dragProvided.dragHandleProps}
                      >
                        <ReferenceItem
                          reference={reference.reference}
                          canEdit={isEditor}
                          onView={() => setViewItem(reference)}
                          onCopy={() => setCopyItem(reference)}
                          editHandler={() => handleEdit(reference)}
                          deleteHandler={() => deleteReference(reference._id)}
                        />
                      </div>
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>
      </div>

      {references.length > 0 && <DownloadReferenceList references={references} />}

      <ViewListModal
        open={viewListOpen}
        handleClose={() => setViewListOpen(false)}
        references={references}
      />

      <ReferenceModal
        open={modalOpen}
        handleClose={() => setModalOpen(false)}
        item={editItem}
        onSave={handleSave}
      />

      <ViewReferenceModal
        open={Boolean(viewItem)}
        handleClose={() => setViewItem(null)}
        item={viewItem}
      />

      <CopyReferenceModal
        open={Boolean(copyItem)}
        handleClose={() => setCopyItem(null)}
        reference={copyItem}
      />
    </div>
  );
};

// The collapsed state: a compact "References Open" strip that sits above the
// first publication card, aligned to its right edge. `onOpen` expands the panel.
export const ReferencesToggle = ({ onOpen }) => (
  <div className="references-open-strip">
    <h2 className="references-title">References</h2>
    <button type="button" className="references-toggle-link" onClick={onOpen}>
      Open
    </button>
    <img
      className="references-toggle-icon"
      src={`${process.env.PUBLIC_URL}/references/Show Icon.svg`}
      alt="Show references"
      onClick={onOpen}
    />
  </div>
);

export default References;
